/**
 * Forecast evolution on the stock Dividend tab (TED #2456): the words, the chart geometry and the
 * `?demo=1` paths. Pure: no React, so the sentences and the scales can be read and checked alone.
 */
import { DivEvolutionPoint, DivEvolutionResponse, DivEvolutionSpark, DivLadderRow, DivOrigin, DivOriginMix, DivStockResponse, DivStockScoreCounts, DivStockScorecard } from "@/models/dividends-v2";
import { fmtDate, fmtMoney } from "./format";

// ─── Words ─────────────────────────────────────────────────────────────────────

export const ORIGIN_LABEL: Record<DivOriginMix, string> = { replayed: "replayed", published: "published", mixed: "replayed and published" };

/** What each origin means, for a tooltip or a footnote (product wording: no method words). */
export const ORIGIN_HELP: Record<DivOrigin, string> = {
    replayed: "Replayed: from before ORTEX published dividend forecasts, rebuilt day by day from only what was known on each day.",
    published: "Published: frozen on the day ORTEX published it.",
};

const year = (iso: string | null | undefined) => (iso ? iso.slice(0, 4) : null);

/** "23 of 24 amounts exact (since 2020, replayed)" for one set of counts. */
export const trackRecordLine = (c: DivStockScoreCounts | null | undefined, origin: DivOriginMix | null | undefined): string | null => {
    if (!c) return null;
    const n = c.n_amount ?? c.n;
    if (!n) return null;
    const since = year(c.since);
    const tail = [since ? `since ${since}` : null, origin ? ORIGIN_LABEL[origin] : null].filter(Boolean).join(", ");
    return `${c.amount_exact ?? 0} of ${n} amounts exact${tail ? ` (${tail})` : ""}`;
};

/** "22 of 24 ex-dates exact, 24 within 3 days". */
export const exDateLine = (c: DivStockScoreCounts | null | undefined): string | null => {
    if (!c) return null;
    const n = c.n_ex ?? c.n;
    if (!n) return null;
    const near = typeof c.ex_within_3 === "number" ? `, ${c.ex_within_3} within 3 days` : "";
    return `${c.ex_exact ?? 0} of ${n} ex-dates exact${near}`;
};

/** The origins of a stock's track record that have counts, longest history first. */
export const scorecardOrigins = (s: DivStockScorecard | null | undefined): { origin: DivOrigin; counts: DivStockScoreCounts }[] =>
    (["replayed", "published"] as DivOrigin[]).flatMap((origin) => {
        const counts = s?.by_origin?.[origin];
        return counts && (counts.n_amount ?? counts.n) ? [{ origin, counts }] : [];
    });

/** Whether the stock has a track record worth a tile. */
export const hasTrackRecord = (p: DivStockResponse): boolean => !!p.scorecard && ((p.scorecard.n_amount ?? p.scorecard.n ?? 0) as number) > 0;

/** "Held the declared 0.26 from 30 Jun 2026, 31 days before the declaration." */
export const settledSentence = (r: Pick<DivEvolutionSpark, "amount_exact_since" | "amount_exact_days_before" | "declared_amount">, currency?: string | null): string | null => {
    const amount = fmtMoney(r.declared_amount, currency);
    if (!amount) return null;
    if (!r.amount_exact_since) return `The forecast did not settle on the declared ${amount} before the declaration.`;
    const days = r.amount_exact_days_before;
    const lead = typeof days === "number" ? `, ${days} ${days === 1 ? "day" : "days"} before the declaration` : "";
    return `Forecast ${amount}, as declared, from ${fmtDate(r.amount_exact_since, true)}${lead}.`;
};

// ─── Chart geometry ────────────────────────────────────────────────────────────

const DAY = 86_400_000;
export const ms = (iso: string): number => Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);

export interface Scale {
    x: (iso: string) => number;
    y: (v: number) => number;
    min: number;
    max: number;
}

/**
 * The scale of a path chart `width` x `height` (with `pad` pixels inside): time across from the
 * first point to the end (the announcement when declared), amounts up, including the band and the
 * declared amount, with a little headroom so a flat path is not drawn on the frame.
 */
export const pathScale = (points: { as_of: string; values: (number | null | undefined)[] }[], end: string | null, extra: (number | null | undefined)[], width: number, height: number, pad = 6): Scale | null => {
    if (!points.length) return null;
    const t0 = ms(points[0].as_of);
    const t1 = Math.max(end ? ms(end) : t0, ms(points[points.length - 1].as_of), t0 + DAY);
    const values = [...points.flatMap((p) => p.values), ...extra].filter((v): v is number => typeof v === "number" && Number.isFinite(v));
    if (!values.length) return null;
    let min = Math.min(...values);
    let max = Math.max(...values);
    const span = max - min || Math.max(Math.abs(max) * 0.04, 0.01);
    min -= span * 0.15;
    max += span * 0.15;
    return {
        x: (iso: string) => pad + ((ms(iso) - t0) / (t1 - t0)) * (width - 2 * pad),
        y: (v: number) => height - pad - ((v - min) / (max - min)) * (height - 2 * pad),
        min,
        max,
    };
};

/** An SVG polyline `d` through the defined points (a gap breaks the line). */
export const linePath = (pts: [number, number | null][]): string => {
    let d = "";
    let open = false;
    for (const [x, y] of pts) {
        if (y === null || !Number.isFinite(y)) {
            open = false;
            continue;
        }
        d += `${open ? "L" : "M"}${x.toFixed(1)},${y.toFixed(1)}`;
        open = true;
    }
    return d;
};

/** The p10..p90 band as one closed SVG path (points without both ends are skipped). */
export const bandPath = (pts: { x: number; lo: number | null; hi: number | null }[]): string => {
    const both = pts.filter((p) => p.lo !== null && p.hi !== null) as { x: number; lo: number; hi: number }[];
    if (both.length < 2) return "";
    const top = both.map((p, i) => `${i ? "L" : "M"}${p.x.toFixed(1)},${p.hi.toFixed(1)}`).join("");
    const bottom = [...both]
        .reverse()
        .map((p) => `L${p.x.toFixed(1)},${p.lo.toFixed(1)}`)
        .join("");
    return `${top}${bottom}Z`;
};

// ─── Demo paths (`?demo=1`) ────────────────────────────────────────────────────

/** In the demo, frozen forecasts from this day on count as published (a go-live date for the example). */
const DEMO_GO_LIVE = "2026-07-01";
const addDays = (iso: string, days: number) => new Date(ms(iso) + days * DAY).toISOString().slice(0, 10);
const round = (v: number, step = 0.005) => Math.round(v / step) * step;

/** The demo path of one ladder row: five frozen forecasts settling on the outcome, plus today's while undeclared. */
export const demoEvolution = (payload: DivStockResponse, row: DivLadderRow): DivEvolutionResponse => {
    const anchor = row.declaration_date ?? row.forecast_declaration_date ?? row.ex_date ?? payload.as_of ?? "2026-10-14";
    const today = payload.as_of ?? "2026-10-14";
    const declared = row.past ? row.declared_amount : null;
    const target = declared ?? row.amount_p50 ?? row.last_amount ?? 0;
    const start = row.last_amount ?? target;
    const exFinal = row.ex_date;
    const exEarly = exFinal ? addDays(exFinal, row.past && row.forecast_ex_date && row.forecast_ex_date !== exFinal ? 0 : 1) : null;
    const horizons: [DivEvolutionPoint["horizon"], number][] = [
        ["first", 95],
        ["d90", 90],
        ["d30", 30],
        ["d7", 7],
        ["d1", 1],
    ];
    const points: DivEvolutionPoint[] = horizons
        .map(([horizon, before], i) => {
            const asOf = addDays(anchor, -before - (horizon === "first" ? 2 : 0));
            const settle = i >= 2 ? 1 : i === 1 ? 0.5 : 0;
            const p50 = round(start + (target - start) * settle, 0.0025);
            const width = Math.max(Math.abs(target) * (0.08 - i * 0.015), 0.0025);
            const pCut = row.p_cut ?? 0.02;
            return {
                as_of: asOf,
                horizon,
                source: "snapshot" as const,
                origin: (asOf >= DEMO_GO_LIVE ? "published" : "replayed") as DivOrigin,
                amount_p10: round(p50 - width, 0.0025),
                amount_p50: p50,
                amount_p90: round(p50 + width, 0.0025),
                ex_date: i >= 2 ? exFinal : exEarly,
                declaration_date: addDays(anchor, i >= 2 ? 0 : 1),
                p_raise: row.p_raise,
                p_hold: row.p_hold,
                p_cut: Math.min(0.95, pCut * (1.4 - i * 0.1)),
                p_omit: row.p_omit,
                p_special: row.p_special,
            };
        })
        .filter((p) => p.as_of <= today);
    if (!row.past && row.status !== "declared") {
        points.push({
            as_of: today,
            horizon: "latest",
            source: "latest",
            origin: "published",
            amount_p10: row.amount_p10,
            amount_p50: row.amount_p50,
            amount_p90: row.amount_p90,
            ex_date: row.ex_date,
            declaration_date: row.declaration_date,
            p_raise: row.p_raise,
            p_hold: row.p_hold,
            p_cut: row.p_cut,
            p_omit: row.p_omit,
            p_special: row.p_special,
        });
    }
    const priced = points.filter((p) => p.amount_p50 !== null);
    let since: string | null = null;
    for (let i = priced.length - 1; i >= 0; i--) {
        if (declared === null || Math.abs((priced[i].amount_p50 as number) - declared) > 1e-9) break;
        since = priced[i].as_of;
    }
    const announced = row.past ? (row.declared_at ?? row.declaration_date)?.slice(0, 10) ?? null : null;
    const origins = new Set(points.map((p) => p.origin));
    const err = (v: number | null | undefined) => (declared && typeof v === "number" ? Math.round((v / declared - 1) * 10000) / 100 : null);
    let exSince: string | null = null;
    for (let i = points.length - 1; i >= 0; i--) {
        if (!row.past || points[i].ex_date !== exFinal) break;
        exSince = points[i].as_of;
    }
    const daysTo = (iso: string | null) => (iso && announced ? Math.max(0, Math.round((ms(announced) - ms(iso)) / DAY)) : null);
    return {
        tradingitem_id: payload.tradingitem_id,
        ticker: payload.ticker,
        exchange_symbol: payload.exchange_symbol,
        company_name: payload.company_name,
        event_key: row.event_key,
        period_label: row.period_label,
        kind: row.kind,
        frequency: row.frequency,
        currency: row.currency,
        country: row.country,
        status: row.past || row.status === "declared" ? "declared" : "forecast",
        origin: origins.size > 1 ? "mixed" : Array.from(origins)[0] ?? null,
        as_of: today,
        points,
        declaration: row.past ? { amount: declared, currency: row.currency, ex_date: row.ex_date, record_date: row.record_date, pay_date: row.pay_date, announced_on: announced } : null,
        convergence: row.past
            ? {
                  declared_amount: declared,
                  declared_ex_date: exFinal,
                  declared_on: announced,
                  points: points.length,
                  first_as_of: priced[0]?.as_of ?? null,
                  first_amount: priced[0]?.amount_p50 ?? null,
                  first_error_pct: err(priced[0]?.amount_p50),
                  last_as_of: priced[priced.length - 1]?.as_of ?? null,
                  last_amount: priced[priced.length - 1]?.amount_p50 ?? null,
                  last_error_pct: err(priced[priced.length - 1]?.amount_p50),
                  amount_exact_points: priced.filter((p) => declared !== null && Math.abs((p.amount_p50 as number) - declared) < 1e-9).length,
                  amount_exact_since: since,
                  amount_exact_days_before: daysTo(since),
                  ex_exact_points: points.filter((p) => p.ex_date === exFinal).length,
                  ex_exact_since: exSince,
                  ex_exact_days_before: daysTo(exSince),
              }
            : null,
        locked: payload.locked,
        upgrade: payload.upgrade,
        published: payload.published ?? true,
        published_countries: payload.published_countries ?? null,
    };
};

/** The ladder spark of a demo path (what TED puts on a past ladder row). */
export const sparkOf = (e: DivEvolutionResponse): DivEvolutionSpark => ({
    points: e.points.filter((p) => p.amount_p50 !== null).map((p) => ({ as_of: p.as_of, amount_p50: p.amount_p50, ex_date: p.ex_date })),
    declared_amount: e.declaration?.amount ?? null,
    origin: e.origin,
    amount_exact_since: e.convergence?.amount_exact_since ?? null,
    amount_exact_days_before: e.convergence?.amount_exact_days_before ?? null,
});

/** A demo payload's past ladder rows with their sparks and origins, and the track record with counts. */
export const withDemoEvolution = (p: DivStockResponse): DivStockResponse => {
    const ladder = (p.ladder ?? []).map((r) => {
        if (!r.past) return { ...r, origin: null, evolution: null };
        const e = demoEvolution(p, r);
        const d30 = e.points.find((pt) => pt.horizon === "d30");
        return { ...r, origin: d30?.origin ?? null, evolution: sparkOf(e) };
    });
    const s = p.scorecard;
    const scorecard: DivStockScorecard | null = s
        ? {
              ...s,
              n_amount: s.n,
              n_ex: s.n,
              amount_within_5: Math.min(s.n ?? 0, (s.amount_exact ?? 0) + 1),
              ex_within_1: Math.min(s.n ?? 0, (s.ex_exact ?? 0) + 1),
              ex_within_3: s.n,
              until: "2026-09-30",
              origin: "mixed",
              note: "30 days before declaration",
              by_origin: {
                  replayed: {
                      amount_exact: (s.amount_exact ?? 0) - 1,
                      amount_within_5: (s.amount_exact ?? 0) - 1,
                      n_amount: (s.n ?? 0) - 2,
                      ex_exact: (s.ex_exact ?? 0) - 1,
                      ex_within_1: (s.ex_exact ?? 0) - 1,
                      ex_within_3: (s.n ?? 0) - 2,
                      n_ex: (s.n ?? 0) - 2,
                      n: (s.n ?? 0) - 2,
                      since: s.since,
                      until: "2026-06-30",
                  },
                  published: { amount_exact: 1, amount_within_5: 2, n_amount: 2, ex_exact: 1, ex_within_1: 2, ex_within_3: 2, n_ex: 2, n: 2, since: "2026-07-01", until: "2026-09-30" },
              },
          }
        : null;
    return { ...p, ladder: ladder.length ? ladder : p.ladder, scorecard };
};
